/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { createContext } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { router } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import {
  styleOverrideEngine,
  isClient,
  getViewportInfo,
  parseBoolean,
} from '@tcp/core/src/utils/index';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import ImageComponent from '@tcp/core/src/components/common/molecules/ModuleContainer/molecules/ImageComponent';
import {
  getCtaBorderRadius,
  getByomHeadereBorderRadius,
  getAspectRatio,
  getModuleModClassSpacing,
  getContainerColumnSpacing,
} from '@tcp/core/src/utils/utils.web';
import CTAItems from '@tcp/core/src/components/common/molecules/ModuleContainer/molecules/CTAItems';

import styles, {
  StyledButton,
  StyledColumn,
  StyledContainer,
  StyledCTAItems,
  StyledCTAContainer,
  StyledCTAColumn,
  StyledAspectRatioBoxInside,
  StyledWrapper,
} from '../styles/ModuleContainer.style';
import {
  COLUMN_5,
  ZERO_WIDTH_COL,
  VARIATION_OVERLAY,
  BORDER_RADIUS_OVERRIDE_MAP,
  HEADER_BORDER_RADIUS_OVERRIDE_MAP,
  COLUMN_SPACING_OVERRIDE_MAP,
} from '../constants/ModuleContainer.constants';
import {
  getColumnSpacing,
  flattenColumnSet,
  getBkgImgUrl,
  getBkgColor,
  getOverlayVariation,
  getNonDecimalNumber,
  getBkgImgSrc,
  getLiveTextBottomView,
  swapKeysOrder,
  getPromoSubCategoryFilterModClassValues,
  extractPromoSubCategoryFilterModuleData,
} from '../utils/utils';
import NonClickableImageBanner from '../molecules/NonclickableImageBanner';
import SafeComponent from '../../../hoc/withErrorBoundary';
import { callNavigateAPIForExternalLinks } from '../../../../../utils/utils';

const AccordionList = dynamic(() =>
  // eslint-disable-next-line extra-rules/no-commented-out-code
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "AccordionList" */ '@tcp/core/src/components/common/molecules/AccordionList'
  )
);

const TextComponent = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "TextComponent" */ '@tcp/core/src/components/common/molecules/ModuleContainer/molecules/TextComponent'
  )
);

const ModuleCountDown = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "module-count-down" */ '@tcp/core/src/components/common/molecules/ModuleCountDown'
  )
);

const CTADropDown = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "CTADropDown" */ '@tcp/core/src/components/common/molecules/ModuleContainer/molecules/CTADropDown'
  )
);

const PromoSubCategoryFilter = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "PromoSubCategoryFilter" */ '@tcp/core/src/components/common/molecules/PromoSubCategoryFilter'
  )
);

export const getBgImagePreloadSrcSet = (isPreload, bkgImgUrl) => {
  const desktopHref = getBkgImgSrc(bkgImgUrl, 'desktop');
  const mobileHref = getBkgImgSrc(bkgImgUrl, 'mobile');
  return isPreload && (bkgImgUrl.bkgImgDUrl || bkgImgUrl.bkgImgMUrl) ? (
    <Head>
      {bkgImgUrl.bkgImgDUrl && (
        <link
          rel="preload"
          as="image"
          href={desktopHref}
          media="(min-width: 768px)"
          key={desktopHref}
        />
      )}
      {(bkgImgUrl.bkgImgDUrl || bkgImgUrl.bkgImgMUrl) && (
        <link
          rel="preload"
          as="image"
          href={mobileHref}
          media="(max-width: 767px)"
          key={mobileHref}
        />
      )}
    </Head>
  ) : null;
};

const getCtaBorderRadiusOverride = (isHpNewModuleDesignEnabled, styleOverrides, overrideMap) => {
  if (isHpNewModuleDesignEnabled && styleOverrides && overrideMap) {
    return getCtaBorderRadius(styleOverrides[overrideMap[0]], styleOverrides[overrideMap[1]]);
  }
  return {};
};
const getHeaderBorderRadiusOverride = (isHpNewModuleDesignEnabled, styleOverrides, overrideMap) => {
  if (isHpNewModuleDesignEnabled && styleOverrides && overrideMap) {
    return getByomHeadereBorderRadius(
      styleOverrides[overrideMap[0]],
      styleOverrides[overrideMap[1]],
      styleOverrides[overrideMap[2]],
      styleOverrides[overrideMap[3]]
    );
  }
  return {};
};

const renderHeaderTextLine = (headerTextLine, { otherProps, assignKeyValue }, key) => {
  const { helpCenterModuleContainer, useRouterNavigation } = otherProps;
  if (!headerTextLine || !headerTextLine.length) return null;
  const { navigation, moduleAnalyticsTag } = otherProps;
  return (
    headerTextLine &&
    headerTextLine.map((headerTextItem, index) => {
      const assignKey = `${assignKeyValue}-${key}-${index}`;
      const noLinkWrap = helpCenterModuleContainer ? !headerTextItem.link : false;
      return (
        <TextComponent
          headerTextLine={headerTextItem}
          navigation={navigation}
          assignKey={assignKey}
          noLinkWrap={noLinkWrap}
          moduleAnalyticsTag={moduleAnalyticsTag}
          useRouterNavigation={useRouterNavigation}
        />
      );
    })
  );
};

const renderCTAItem = (ctaItem, assignKeyValue) => {
  if (!ctaItem) return null;
  const { button } = ctaItem;
  return (
    <StyledButton buttonVariation="fixed-width" cta={button} key={assignKeyValue}>
      {button.text}
    </StyledButton>
  );
};

const renderCTAItemComposite = (ctaItems, { otherProps, assignKeyValue }) => {
  const {
    styleOverrides,
    isHpNewModuleDesignEnabled,
    colName,
    ctaVariationValue,
    isCtaOverlayVariation,
  } = otherProps;
  const { isMobile } = isClient() && getViewportInfo();
  const ctaItemFilteredObj = ctaItems && ctaItems.filter((item) => item.button);
  const ctaTypeValue = ctaItemFilteredObj && ctaItemFilteredObj[0] && ctaItemFilteredObj[0].ctaType;
  const ctaBgImageFilteredObj = ctaItems && ctaItems.filter((item) => item.ctaBackgroundImage);
  const ctaBgImage = ctaBgImageFilteredObj.length !== 0 && ctaBgImageFilteredObj[0];
  const ctaBorderRadiusOverride =
    isHpNewModuleDesignEnabled && !isEmpty(styleOverrides) && !isMobile
      ? getCtaBorderRadiusOverride(
          isHpNewModuleDesignEnabled,
          styleOverrides,
          BORDER_RADIUS_OVERRIDE_MAP[colName]
        )
      : {};
  const navBgStyleOverrides = styleOverrides['nav-bg'] || {};

  return ctaItemFilteredObj ? (
    <StyledCTAContainer
      className="styled-cta-container"
      ctaBgImageObj={ctaBgImage}
      ctaTypeValue={ctaTypeValue}
      ctaVariationValue={ctaVariationValue}
      isCtaOverlayVariation={isCtaOverlayVariation}
      style={Object.assign(navBgStyleOverrides, ctaBorderRadiusOverride)}
    >
      <StyledCTAItems
        className="styled-cta-items"
        ctaTypeValue={ctaTypeValue}
        ctaEnableVariation={ctaItemFilteredObj}
        style={Object.assign(navBgStyleOverrides, ctaBorderRadiusOverride)}
      >
        <CTAItems
          ctaItems={ctaItemFilteredObj}
          styleOverrides={styleOverrides}
          assignKeyValue={assignKeyValue}
          {...otherProps}
        />
      </StyledCTAItems>
    </StyledCTAContainer>
  ) : null;
};

const renderLargeCompImageSimpleCarousel = (
  largeCompImageSimpleCarousel,
  { otherProps, assignKeyValue, transformedCTAItemsComposite }
) => {
  return largeCompImageSimpleCarousel ? (
    <ImageComponent
      largeCompImageSimpleCarousel={largeCompImageSimpleCarousel}
      assignKeyValue={assignKeyValue}
      transformedCTAItemsComposite={transformedCTAItemsComposite}
      {...otherProps}
    />
  ) : null;
};

const renderCountDownComposite = (countdown, { otherProps, assignKeyValue }) => {
  return countdown && countdown.length > 0 ? (
    <ModuleCountDown countdown={countdown[0]} assignKeyValue={assignKeyValue} {...otherProps} />
  ) : null;
};

const renderConditionalComponent = (helpCenterModuleContainer) =>
  helpCenterModuleContainer ? React.Fragment : StyledAspectRatioBoxInside;

const transformArrayToObject = (arr) => {
  return arr.reduce((acc, item) => {
    const slide = item?.slide || 'none';
    if (!acc[slide]) {
      acc[slide] = [];
    }
    acc[slide].push(item);
    return acc;
  }, {});
};

/* eslint-disable sonarjs/cognitive-complexity */
const renderCompositeData = (composite, composites, otherProps, { key, index }) => {
  const { aspectRatioObj, helpCenterModuleContainer, colName, slotIndex } = otherProps;
  const assignKeyValue = `${slotIndex}-${colName}-${composite}-${index}`;
  const StyledAspectRatioBox = renderConditionalComponent(helpCenterModuleContainer);
  const transformedCTAItems =
    !isEmpty(composites.ctaItemsComposite) &&
    // eslint-disable-next-line dot-notation
    transformArrayToObject(composites.ctaItemsComposite)['none'];
  const transformedCTAItemsComposite =
    !isEmpty(composites.ctaItemsComposite) && transformArrayToObject(composites.ctaItemsComposite);

  switch (composite) {
    case 'headerTextLine':
      return composites.headerTextLine ? (
        <StyledAspectRatioBox aspectRatioObj={aspectRatioObj} key={assignKeyValue}>
          {renderHeaderTextLine(composites.headerTextLine, { otherProps, assignKeyValue }, key)}
        </StyledAspectRatioBox>
      ) : null;
    case 'ctaItem':
      return composites.ctaItem ? (
        <StyledAspectRatioBox aspectRatioObj={aspectRatioObj} key={assignKeyValue}>
          {renderCTAItem(composites.ctaItem, assignKeyValue)}
        </StyledAspectRatioBox>
      ) : null;
    case 'ctaItemsComposite':
      return transformedCTAItems ? (
        <StyledAspectRatioBox aspectRatioObj={aspectRatioObj} key={assignKeyValue}>
          {renderCTAItemComposite(transformedCTAItems, { otherProps, assignKeyValue })}
        </StyledAspectRatioBox>
      ) : null;
    case 'largeCompImageSimpleCarouselComposite':
      return composites.largeCompImageSimpleCarouselComposite ? (
        <StyledAspectRatioBox aspectRatioObj={aspectRatioObj} key={assignKeyValue}>
          {renderLargeCompImageSimpleCarousel(composites.largeCompImageSimpleCarouselComposite, {
            otherProps,
            assignKeyValue,
            transformedCTAItemsComposite,
          })}
        </StyledAspectRatioBox>
      ) : null;
    case 'countdown':
      return composites.countdown ? (
        <StyledAspectRatioBox aspectRatioObj={aspectRatioObj} key={assignKeyValue}>
          {renderCountDownComposite(composites.countdown, { otherProps, assignKeyValue })}
        </StyledAspectRatioBox>
      ) : null;
    case 'ctaDropdownComposite':
      return !isEmpty(composites.ctaDropdownComposite) ? (
        <StyledAspectRatioBox aspectRatioObj={aspectRatioObj} key={assignKeyValue}>
          <CTADropDown
            ctaDropdownGroup={composites.ctaDropdownComposite}
            keyVal={assignKeyValue}
            otherProps={otherProps}
          />
        </StyledAspectRatioBox>
      ) : null;
    default:
      return <></>;
  }
};

const renderImage = (imageProps, otherProps) => {
  if (!imageProps) return null;
  return <NonClickableImageBanner otherProps={otherProps} imageData={imageProps} />;
};

const renderComposites = (composites, otherProps) => {
  const { isLiveTextBottomView = false } = otherProps;
  const compositeKeys = composites && Object.keys(composites);
  const updatedCompositeKeys = swapKeysOrder(
    compositeKeys,
    'headerTextLine',
    'largeCompImageSimpleCarouselComposite',
    isLiveTextBottomView
  );

  return (
    updatedCompositeKeys &&
    updatedCompositeKeys.map((composite, index) => {
      const key = `${composite}-${index}`;
      return renderCompositeData(composite, composites, otherProps, { key, index });
    })
  );
};

const renderColumnData = (colKey, column, otherProps) => {
  switch (colKey) {
    case 'image':
      return renderImage(column.image, otherProps);
    case 'composites':
      return renderComposites(column.composites, otherProps);
    default:
      return <></>;
  }
};

const calNumericWidth = (helpCenterModuleContainer, columnWidthVal) => {
  if (helpCenterModuleContainer) return parseInt(columnWidthVal, 10);
  return null;
};

const getVariations = ({ column, colName }) => {
  const headerVariationValue =
    column &&
    column.composites &&
    column.composites.headerTextLine !== null &&
    getOverlayVariation(column.composites.headerTextLine);
  const ctaVariationValue =
    column &&
    column.composites &&
    column.composites.ctaItemsComposite !== null &&
    getOverlayVariation(column.composites.ctaItemsComposite);
  const isHeaderOverlayVariation = headerVariationValue?.variation === VARIATION_OVERLAY;
  const isCtaOverlayVariation =
    ctaVariationValue?.variation === VARIATION_OVERLAY && colName !== COLUMN_5;

  return {
    headerVariationValue,
    ctaVariationValue,
    isHeaderOverlayVariation,
    isCtaOverlayVariation,
  };
};

const checkCtaItems = (column) =>
  column && column.composites && column.composites.ctaItemsComposite;

const getDesktopView = (columnWidthVal, columnWidthMVal) => {
  const columnWidthValue = getNonDecimalNumber(columnWidthVal);
  const columnWidthMValue = getNonDecimalNumber(columnWidthMVal);
  return columnWidthValue !== ZERO_WIDTH_COL && columnWidthMValue === ZERO_WIDTH_COL;
};

const getMobileView = (columnWidthVal, columnWidthMVal) => {
  const columnWidthValue = getNonDecimalNumber(columnWidthVal);
  const columnWidthMValue = getNonDecimalNumber(columnWidthMVal);
  return columnWidthMValue !== ZERO_WIDTH_COL && columnWidthValue === ZERO_WIDTH_COL;
};

const getCommonView = (columnWidthVal, columnWidthMVal) => {
  const columnWidthValue = getNonDecimalNumber(columnWidthVal);
  const columnWidthMValue = getNonDecimalNumber(columnWidthMVal);
  return columnWidthMValue !== ZERO_WIDTH_COL && columnWidthValue !== ZERO_WIDTH_COL;
};

const renderChildComp = (
  columnKeys,
  column,
  otherProps,
  colName,
  isCtaOverlayVariation,
  ctaVariationValue,
  aspectRatioObj,
  isDesktopOnly,
  isMobileOnly,
  { isCommonView, isLiveTextBottomView }
) =>
  columnKeys.map((colKey) => {
    return renderColumnData(colKey, column, {
      ...otherProps,
      colName,
      isCtaOverlayVariation,
      ctaVariationValue,
      aspectRatioObj,
      isDesktopOnly,
      isMobileOnly,
      isCommonView,
      isLiveTextBottomView,
    });
  });

const getDesktopColumnView = (isDesktop, columnWidthVal) => {
  const columnWidthValue = getNonDecimalNumber(columnWidthVal);
  return isDesktop && columnWidthValue !== ZERO_WIDTH_COL;
};

const getTabletColumnView = (isDesktopView, isTablet, columnWidthVal) => {
  const columnWidthValue = getNonDecimalNumber(columnWidthVal);
  return !isDesktopView && isTablet && columnWidthValue !== ZERO_WIDTH_COL;
};

const getMobileColumnView = (isTabletView, isMobile, columnWidthMVal) => {
  const columnWidthMValue = getNonDecimalNumber(columnWidthMVal);
  return !isTabletView && isMobile && columnWidthMValue !== ZERO_WIDTH_COL;
};

const computeHeaderBorderRadius = (isHpNewModuleDesignEnabled, styleOverrides, colName) =>
  isHpNewModuleDesignEnabled && !isEmpty(styleOverrides)
    ? getHeaderBorderRadiusOverride(
        isHpNewModuleDesignEnabled,
        styleOverrides,
        HEADER_BORDER_RADIUS_OVERRIDE_MAP[colName]
      )
    : {};

const getColumnSpacingContainerOverride = (styleOverrides, overrideMap) => {
  if (styleOverrides && overrideMap) {
    return getContainerColumnSpacing(
      styleOverrides[overrideMap[0]],
      styleOverrides[overrideMap[1]]
    );
  }
  return {};
};

const computeColumnSpacing = (styleOverrides, colName) =>
  !isEmpty(styleOverrides)
    ? getColumnSpacingContainerOverride(styleOverrides, COLUMN_SPACING_OVERRIDE_MAP[colName])
    : {};

const renderColumnComponent = (commonProps, remainingProps) => {
  const {
    numericWidth,
    columnSpacing,
    isHeaderOverlayVariation,
    headerVariationValue,
    isCtaItems,
    borderUrl,
    borderWidth,
    borderColor,
    bkgImgUrl,
    bkgColor,
    headerOverrideStyle,
    aspectRatioObj,
    columnKeys,
    column,
    otherProps,
    colName,
    isCtaOverlayVariation,
    ctaVariationValue,
    isDesktopOnly,
    isMobileOnly,
    isCommonView,
    index,
    isLiveTextBottomView,
    containerSpacingColumnOverride,
  } = commonProps;

  const { checkSsrCount, slotIndex, isPreload } = otherProps;
  const bgImagePreloadSrcset = getBgImagePreloadSrcSet(isPreload, bkgImgUrl);
  return (
    <>
      {bgImagePreloadSrcset}
      <StyledColumn
        key={`${index}-${colName}-${slotIndex}`}
        numericWidth={numericWidth}
        spacing={columnSpacing}
        className={`styled-column ${
          isLiveTextBottomView ? `styled-col-live-text-bottom-view` : ``
        }`}
        isHeaderOverlayVariation={isHeaderOverlayVariation}
        headerVariationValue={headerVariationValue}
        isCtaItems={isCtaItems}
        borderUrl={borderUrl}
        borderWidth={borderWidth}
        borderColor={borderColor}
        bkgImgUrl={bkgImgUrl}
        bkgColor={bkgColor}
        style={headerOverrideStyle}
        aspectRatioObj={aspectRatioObj}
        checkSsrCount={checkSsrCount}
        spacingColumnOverride={containerSpacingColumnOverride}
        {...remainingProps}
      >
        {renderChildComp(
          columnKeys,
          column,
          otherProps,
          colName,
          isCtaOverlayVariation,
          ctaVariationValue,
          aspectRatioObj,
          isDesktopOnly,
          isMobileOnly,
          { isCommonView, isLiveTextBottomView }
        )}
      </StyledColumn>
    </>
  );
};

const getBorder = (column) => (column && column.border) || {};
const getBorderColor = (border) => border.color || {};
const getBorderImage = (border) => border.image || {};

const getViewportRenderProps = (
  isDesktopViewPort,
  isTabletViewPort,
  isMobileViewPort,
  columnWidthVal,
  columnWidthMVal
) => {
  const isDesktopView = getDesktopColumnView(isDesktopViewPort, columnWidthVal);
  const isTabletView = getTabletColumnView(isDesktopView, isTabletViewPort, columnWidthVal);
  const isMobileView = getMobileColumnView(isTabletView, isMobileViewPort, columnWidthMVal);

  if (isDesktopView) {
    return { viewType: 'desktop', remainingProps: { width: columnWidthVal } };
  }
  if (isTabletView) {
    return { viewType: 'tablet', remainingProps: { widthT: columnWidthVal } };
  }
  if (isMobileView) {
    return { viewType: 'mobile', remainingProps: { widthM: columnWidthMVal } };
  }
  return null;
};

const renderColumns = (columns, otherProps) => {
  return columns.map((column, index) => {
    const columnSet = flattenColumnSet(column);
    const { width: columnWidthVal, widthM: columnWidthMVal, colName } = columnSet;
    const columnKeys = Object.keys(column);
    const columnSpacing = getColumnSpacing(columnSet);
    const border = getBorder(column);
    const { url: desktopUrl, url_m: mobileUrl, url_t: tabletUrl } = getBorderImage(border);
    const { color: borderColor } = getBorderColor(border);
    const borderSet = flattenColumnSet(border);
    const { width, widthT, widthM } = borderSet || {};
    const borderUrl = { desktopUrl, mobileUrl, tabletUrl };
    const borderWidth = { width, widthT, widthM };
    const isCtaItems = checkCtaItems(column);
    const isDesktopOnly = getDesktopView(columnWidthVal, columnWidthMVal);
    const isMobileOnly = getMobileView(columnWidthVal, columnWidthMVal);
    const isCommonView = getCommonView(columnWidthVal, columnWidthMVal);

    const {
      headerVariationValue,
      ctaVariationValue,
      isHeaderOverlayVariation,
      isCtaOverlayVariation,
    } = getVariations({ column, colName });

    const background = (column && column.background) || {};
    const {
      styleOverrides,
      isHpNewModuleDesignEnabled,
      helpCenterModuleContainer,
      isDesktopViewPort,
      isTabletViewPort,
      isMobileViewPort,
      checkSsrCount,
      isLiveTextBottomView,
    } = otherProps;
    const headerBorderRadiusOverride = computeHeaderBorderRadius(
      isHpNewModuleDesignEnabled,
      styleOverrides,
      colName
    );
    const containerSpacingColumnOverride = computeColumnSpacing(styleOverrides, colName);
    const headerOverrideStyle = isHeaderOverlayVariation ? {} : headerBorderRadiusOverride;

    const numericWidth = calNumericWidth(helpCenterModuleContainer, columnWidthVal);

    const aspectRatioObj = getAspectRatio(column?.background?.image);

    const bkgImgUrl = getBkgImgUrl(background);
    const bkgColor = getBkgColor(background);

    const commonProps = {
      numericWidth,
      columnSpacing,
      isHeaderOverlayVariation,
      headerVariationValue,
      isCtaItems,
      borderUrl,
      borderWidth,
      borderColor,
      bkgImgUrl,
      bkgColor,
      headerOverrideStyle,
      aspectRatioObj,
      columnKeys,
      column,
      otherProps,
      colName,
      isCtaOverlayVariation,
      ctaVariationValue,
      isDesktopOnly,
      isMobileOnly,
      isCommonView,
      index,
      isLiveTextBottomView,
      containerSpacingColumnOverride,
    };

    const isColumnData =
      (!isEmpty(column) && !isEmpty(column.composites)) || !isEmpty(column.image);

    if (!isColumnData) {
      return <></>;
    }

    if (checkSsrCount) {
      const viewportProps = getViewportRenderProps(
        isDesktopViewPort,
        isTabletViewPort,
        isMobileViewPort,
        columnWidthVal,
        columnWidthMVal
      );
      if (viewportProps) {
        return renderColumnComponent(commonProps, viewportProps.remainingProps);
      }
    } else {
      const remainingProps = {
        width: columnWidthVal,
        widthT: columnWidthVal,
        widthM: columnWidthMVal,
      };
      return renderColumnComponent(commonProps, remainingProps);
    }

    return <></>;
  });
};

export const PropsContext = createContext();

class ModuleContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isDesktopViewPort: false,
      isTabletViewPort: false,
      isMobileViewPort: true,
    };
    this.updateViewport = this.updateViewport.bind(this);
  }

  componentDidMount() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateViewport);
  }

  updateViewport() {
    this.setState({
      isDesktopViewPort: window.innerWidth >= 1024,
      isTabletViewPort: window.innerWidth >= 768,
      isMobileViewPort: window.innerWidth < 768,
    });
  }

  render() {
    const { isDesktopViewPort, isTabletViewPort, isMobileViewPort } = this.state;
    const {
      allColumns,
      ctaOnlyColumn,
      moduleClassName = '',
      className,
      isHpNewDesignCTAEnabled,
      isHpNewModuleDesignEnabled,
      disableVideosForAppleDevice,
      slotIndex,
      mouseMoved,
      helpCenterModuleContainer,
      firstModuleContainer,
      accordionIndex,
      selectedAccordionIndex,
      setSelectedAccordionIndex,
      isMobile,
      isHelpCenterHomePage,
      isPreload,
      ssrCount,
      isHomePage,
      useRouterNavigation,
      moduleAnalyticsTag,
      fromHplp,
      setFromHplpFlag,
      slot,
      pageSlotName,
      isDoubleTile,
      isSingleTile,
      isInGridPromo,
      isPlpTopPromos,
      isFullWidthTile,
      isBundleStaticPage,
      isStaticContentPage,
    } = this.props;
    const checkSsrCount = slotIndex > ssrCount;
    const styleOverrides = !isEmpty(moduleClassName)
      ? styleOverrideEngine(moduleClassName, 'ModuleContainer')
      : {};
    const isLiveTextBottomView = getLiveTextBottomView(styleOverrides);
    const {
      imagePlacement,
      heroImageWidth,
      isCardAnimation,
      isHeroImageBannerSetup,
      isPromoSubCategoryFilterSetup,
    } = getPromoSubCategoryFilterModClassValues(styleOverrides);
    const { marginTop = {}, marginBottom = {} } = getModuleModClassSpacing(styleOverrides);
    const otherProps = {
      styleOverrides,
      isHpNewDesignCTAEnabled,
      isHpNewModuleDesignEnabled,
      disableVideosForAppleDevice,
      slotIndex,
      mouseMoved,
      helpCenterModuleContainer,
      isPreload,
      isDesktopViewPort,
      isTabletViewPort,
      isMobileViewPort,
      checkSsrCount,
      isHomePage,
      isLiveTextBottomView,
      useRouterNavigation,
      moduleAnalyticsTag,
      slot,
      pageSlotName,
      isDoubleTile,
      isSingleTile,
      isInGridPromo,
      isPlpTopPromos,
      isFullWidthTile,
      isBundleStaticPage,
      isStaticContentPage,
    };
    const { zoomHover = {} } = styleOverrides || {};

    if (isPromoSubCategoryFilterSetup) {
      const { heroImageBanner, subCategoryFilterImages, moduleHeaderTitle } =
        extractPromoSubCategoryFilterModuleData(isHeroImageBannerSetup, allColumns);

      return (
        <PromoSubCategoryFilter
          routerProps={router}
          fromHorizontalPlp={fromHplp}
          heroImageWidth={heroImageWidth}
          isCardAnimation={isCardAnimation}
          heroImageBanner={heroImageBanner}
          heroImagePlacement={imagePlacement}
          moduleHeaderTitle={moduleHeaderTitle}
          setFromHorizontalPlp={setFromHplpFlag}
          subCategoryFilterImages={subCategoryFilterImages}
        />
      );
    }

    return (
      <PropsContext.Provider value={this.props}>
        {isMobile && helpCenterModuleContainer && isHelpCenterHomePage ? (
          <>
            <AccordionList
              isMobile={isMobile}
              isHelpCenterHomePage={isHelpCenterHomePage}
              helpCenterModuleContainer={helpCenterModuleContainer}
              defaultOpenIndex={firstModuleContainer ? '0' : ''}
              accordionIndex={accordionIndex}
              selectedAccordionIndex={selectedAccordionIndex}
              setSelectedAccordionIndex={setSelectedAccordionIndex}
              accordionItems={allColumns
                ?.map((column) => {
                  return column?.composites?.headerTextLine?.[0]?.textItems?.reduce(
                    (prev, curr) => {
                      return `${prev} ${curr.text} `;
                    },
                    ''
                  );
                })
                .map((text) => {
                  return {
                    header: {
                      id: text
                        ?.split(' ')
                        .filter((txt) => txt.length)
                        .join('_'),
                      title: text,
                    },
                  };
                })}
            >
              {[...renderColumns(allColumns, otherProps)]}
            </AccordionList>
            {ctaOnlyColumn.length > 0 && (
              <StyledCTAColumn
                className={`${className} styled-cta-only-column`}
                style={styleOverrides['nav-bg']}
                onClick={callNavigateAPIForExternalLinks}
              >
                {renderColumns(ctaOnlyColumn, otherProps)}
              </StyledCTAColumn>
            )}
          </>
        ) : (
          <StyledWrapper
            zoomHover={parseBoolean(zoomHover?.zoomHover)}
            spacingTop={marginTop}
            spacingBottom={marginBottom}
          >
            <StyledContainer
              className={`${className} ${moduleClassName} styled-container`}
              onClick={callNavigateAPIForExternalLinks}
            >
              {renderColumns(allColumns, otherProps)}
            </StyledContainer>
            {ctaOnlyColumn.length > 0 && (
              <StyledCTAColumn
                className={`${className} styled-cta-only-column ${
                  isLiveTextBottomView ? `col5-live-text-bottom-view` : ``
                }`}
                style={styleOverrides['nav-bg']}
                onClick={callNavigateAPIForExternalLinks}
              >
                {renderColumns(ctaOnlyColumn, otherProps)}
              </StyledCTAColumn>
            )}
          </StyledWrapper>
        )}
      </PropsContext.Provider>
    );
  }
}

ModuleContainer.propTypes = {
  moduleClassName: PropTypes.string,
  allColumns: PropTypes.arrayOf({}),
  ctaOnlyColumn: PropTypes.arrayOf({}),
  className: PropTypes.string,
  isHpNewDesignCTAEnabled: PropTypes.bool.isRequired,
  isHpNewModuleDesignEnabled: PropTypes.bool.isRequired,
  disableVideosForAppleDevice: PropTypes.bool.isRequired,
  slotIndex: PropTypes.number.isRequired,
  mouseMoved: PropTypes.bool.isRequired,
  helpCenterModuleContainer: PropTypes.bool,
  firstModuleContainer: PropTypes.bool,
  accordionIndex: PropTypes.number,
  selectedAccordionIndex: PropTypes.number,
  setSelectedAccordionIndex: PropTypes.func,
  isMobile: PropTypes.bool,
  isPreload: PropTypes.bool,
  useRouterNavigation: PropTypes.bool,
};

ModuleContainer.defaultProps = {
  moduleClassName: '',
  allColumns: [],
  ctaOnlyColumn: [],
  className: '',
  helpCenterModuleContainer: false,
  firstModuleContainer: false,
  accordionIndex: -1,
  selectedAccordionIndex: -1,
  setSelectedAccordionIndex: () => {},
  isMobile: false,
  isPreload: false,
  useRouterNavigation: false,
};

ModuleContainer.displayName = 'ModuleContainer';

export default withStyles(SafeComponent(ModuleContainer), styles);
export { ModuleContainer as ModuleContainerVanilla };
